import {
  CommandProps,
  Node,
  mergeAttributes,
  callOrReturn,
  InputRule,
  Range,
  ExtendedRegExpMatchArray,
} from '@tiptap/core';
import { EditorState, TextSelection } from '@tiptap/pm/state';
import { MarkdownNodeSpec } from 'tiptap-markdown';
import texmath, { RuleName } from './texmath';

import { createMathView, mathPlugin } from './math-plugin';
import { Fragment } from 'prosemirror-model';
import { MathToken } from '@scale/shared/types/config';

export const inputRegex = /(?:^|\s)(\$(?!\s+\$)(?<content>(?:[^$]+))\$(?!\s+\$))$/;

/**
 * Matches inline code while pasting.
 */
export const pasteRegex = /(?:^|\s)(\$(?!\s+\$)((?:[^$]+))\$(?!\s+\$))/g;

type MathMarkOptions = { allowedRules: RuleName[] };

type MathMarkAttributes = {
  open: string;
  close: string;
};

function extractTextFromFragment(fragment: Fragment) {
  let text = '';
  fragment.forEach(node => {
    if (node.isText) {
      text += node.text;
    } else if (node.isLeaf) {
      text += node.textContent; // Handle leaf nodes (e.g., images) if needed
    }
    // Handle nested fragments if necessary
  });
  return text;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    math_inline: {
      setFormulaMark: (attributes: MathMarkAttributes) => ReturnType;
      toggleFormulaMark: () => ReturnType;
      unsetFormulaMark: () => ReturnType;
    };
  }
}

function getMathMarkAttributes(type: MathToken): MathMarkAttributes {
  switch (type) {
    case MathToken.DOLLARS:
      return {
        open: '$',
        close: '$',
      };
    case MathToken.BRACKETS:
      return {
        open: '\\(',
        close: '\\)',
      };
    case MathToken.ESCAPED_BRACKETS:
      return {
        open: '\\\\(',
        close: '\\\\)',
      };
  }
}

export default Node.create<MathMarkOptions>({
  name: 'math_inline',
  group: 'inline math',
  content: 'text*', // important!
  inline: true, // important!
  atom: true, // important!

  addOptions() {
    return {
      allowedRules: [MathToken.DOLLARS],
    };
  },

  addStorage(): { markdown: MarkdownNodeSpec } {
    return {
      markdown: {
        serialize(state, node) {
          state.text(`${node.attrs.open}${node.textContent}${node.attrs.close}`, false);
        },
        parse: {
          setup(markdownIt) {
            const delimiters = this.options.allowedRules;
            // @ts-ignore
            markdownIt.use(texmath, {
              delimiters,
              rules: 'inline',
            });
          },
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'math-inline', // important!
        getAttrs: element => ({
          open: element.getAttribute('open') || '$',
          close: element.getAttribute('close') || '$',
        }),
      },
    ];
  },

  addAttributes() {
    return {
      open: {
        default: '$',
        renderHTML: attributes => {
          return {
            open: attributes.open,
          };
        },
      },
      close: {
        default: '$',
        renderHTML: attributes => {
          return {
            close: attributes.close,
          };
        },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ['math-inline', mergeAttributes({ class: 'math-node' }, HTMLAttributes), 0];
  },

  addProseMirrorPlugins() {
    return [mathPlugin];
  },

  addNodeView() {
    return ({ node, getPos }) => {
      const nodeViewRenderer = createMathView(false);
      return nodeViewRenderer(node, this.editor.view, getPos);
    };
  },

  addInputRules() {
    const handleReplacement = (
      state: EditorState,
      range: Range,
      match: ExtendedRegExpMatchArray,
      open: string,
      close: string,
    ) => {
      const getAttributes = {
        open,
        close,
      };
      const nodeType = this.type;
      const start = range.from;
      const end = range.to;
      const $start = state.doc.resolve(start);
      const index = $start.index();
      const $end = state.doc.resolve(end);
      // get attrs
      const attributes = callOrReturn(getAttributes, undefined, match) || {};
      // check if replacement valid
      if (!$start.parent.canReplaceWith(index, $end.index(), nodeType)) {
        return null;
      }
      // perform replacement
      state.tr.replaceRangeWith(
        start,
        end,
        nodeType.create(attributes, nodeType.schema.text(match[1])),
      );
    };
    const rules: RuleName[] = this.options.allowedRules;
    const inputRules: Record<RuleName, InputRule> = {
      [MathToken.DOLLARS]: new InputRule({
        find: /\$(.+)\$/,
        handler: ({ state, range, match }) => {
          handleReplacement(state, range, match, '$', '$');
        },
      }),
      [MathToken.ESCAPED_BRACKETS]: new InputRule({
        find: /\\\\\((.+)\\\\\)/,
        handler: ({ state, range, match }) => {
          handleReplacement(state, range, match, '\\\\(', '\\\\)');
        },
      }),
      [MathToken.BRACKETS]: new InputRule({
        find: /(?<!\\)\\\((.+?)\\\)/,
        handler: ({ state, range, match }) => {
          handleReplacement(state, range, match, '\\(', '\\)');
        },
      }),
      gitlab: new InputRule({
        find: /\$`(.+)`\$/,
        handler: ({ state, range, match }) => {
          handleReplacement(state, range, match, '$`', '`$');
        },
      }),
    };

    return rules.map(ruleName => inputRules[ruleName]);
  },

  addCommands() {
    return {
      setFormulaMark:
        (attributes: MathMarkAttributes) =>
        ({ commands }: CommandProps) => {
          return commands.setMark(this.name, attributes);
        },
      toggleFormulaMark:
        () =>
        ({ state, dispatch }) => {
          const { selection } = state;
          const { from, to } = selection;

          const defaultNodeType =
            this.editor.storage.markdownExtensions.mathToken ?? MathToken.DOLLARS;

          const attrs = getMathMarkAttributes(defaultNodeType);

          // Check if the selection is within a math inline node
          let isMathInline = false;
          let mathInlinePos: number | null = null;

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.type === this.type) {
              isMathInline = true;
              mathInlinePos = pos;
            }
          });

          if (isMathInline && mathInlinePos !== null) {
            // If inside a math inline node, lift its content out without removing the content
            if (dispatch) {
              const tr = state.tr;
              const mathNode = state.doc.nodeAt(mathInlinePos);

              // Only remove the wrapping node, keeping the content intact
              if (mathNode) {
                const textNode = state.schema.text(extractTextFromFragment(mathNode.content));
                tr.replaceRangeWith(mathInlinePos, mathInlinePos + mathNode.nodeSize, textNode);
              }

              dispatch(tr);
            }
            return true;
          } else {
            // Wrap the selection in a math inline node
            if (dispatch) {
              const { tr } = state;
              const selectedContent = state.doc.slice(from, to).content;
              const mathNode = this.type.create(attrs, selectedContent);
              tr.replaceRangeWith(from, to, mathNode);

              // Set the selection inside the new node
              tr.setSelection(TextSelection.create(tr.doc, from + 1, from + 1));
              dispatch(tr);
            }
            return true;
          }
        },
      unsetFormulaMark:
        () =>
        ({ commands }: CommandProps) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});
